/**
 * rc135 — Web Push para el panel admin instalable (PWA).
 * Portado de ByteOps. iOS exige: app instalada en pantalla de inicio (standalone)
 * y que `activarPush()` se llame desde un toque del usuario.
 */
import { apiCall } from "@/lib/api";

export function esIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export function instalada(): boolean {
  return window.matchMedia("(display-mode: standalone)").matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
}

export function pushSoportado(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function permiso(): NotificationPermission | "no-soportado" {
  if (!("Notification" in window)) return "no-soportado";
  return Notification.permission;
}

function b64ToU8(b64: string): ArrayBuffer {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  const buf = new ArrayBuffer(raw.length);
  const out = new Uint8Array(buf);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return buf;
}

export async function registrarSW(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register(`/sw.js?v=${__APP_VERSION__}`, { scope: "/" });
  } catch {
    return null;
  }
}

async function enviarSuscripcion(sub: PushSubscription): Promise<void> {
  const j = sub.toJSON();
  await apiCall("post", "/api/v1/notifications/push/suscribir", {
    json: { endpoint: j.endpoint, p256dh: j.keys?.p256dh, auth: j.keys?.auth, user_agent: navigator.userAgent.slice(0, 300) },
  });
}

/** Debe llamarse desde un toque del usuario (iOS lo exige). Devuelve "ok" o un mensaje para mostrar. */
export async function activarPush(): Promise<string> {
  if (!pushSoportado()) {
    if (esIOS() && !instalada()) return "Primero instala ByteISP en tu pantalla de inicio: Compartir → Agregar a inicio. Luego ábrelo desde ahí y vuelve a intentar.";
    return "Este navegador no soporta notificaciones.";
  }
  const p = await Notification.requestPermission();
  if (p !== "granted") return "No diste permiso de notificaciones. Puedes activarlo en los ajustes del navegador o del teléfono.";
  const reg = (await navigator.serviceWorker.getRegistration()) || (await registrarSW());
  if (!reg) return "No se pudo registrar el service worker.";
  await navigator.serviceWorker.ready;
  const { public_key } = await apiCall<{ public_key: string }>("get", "/api/v1/notifications/push/llave");
  let sub = await reg.pushManager.getSubscription();
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToU8(public_key) });
  await enviarSuscripcion(sub);
  return "ok";
}

export async function pushActivo(): Promise<boolean> {
  if (!pushSoportado() || Notification.permission !== "granted") return false;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  return !!sub;
}

/** Si ya hay permiso, re-sincroniza la suscripción con el servidor (por si cambió de usuario). */
export async function resincronizarPush(): Promise<void> {
  try {
    if (!(await pushActivo())) return;
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    if (sub) await enviarSuscripcion(sub);
  } catch {
    /* silencioso */
  }
}

export async function bajaPush(): Promise<void> {
  const reg = await navigator.serviceWorker?.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await apiCall("post", "/api/v1/notifications/push/baja", { json: { endpoint: sub.endpoint } }).catch(() => {});
    await sub.unsubscribe().catch(() => {});
  }
}

// ── rc136 — Instalación nativa (Android/Chrome/Edge: `beforeinstallprompt`) ──
// Chrome dispara el evento UNA vez, temprano (a veces antes de que monte React),
// así que lo capturamos a nivel módulo y lo guardamos. iOS/Safari no lo emite:
// ahí la instalación es manual (Compartir → Agregar a inicio).
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let promptPendiente: BeforeInstallPromptEvent | null = null;
const oyentes = new Set<() => void>();
function avisar() {
  oyentes.forEach((f) => f());
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault(); // sin esto Chrome muestra su mini-barra cuando quiere
    promptPendiente = e as BeforeInstallPromptEvent;
    avisar();
  });
  window.addEventListener("appinstalled", () => {
    promptPendiente = null;
    avisar();
  });
}

/** true cuando el navegador ofrece instalar con un botón propio. */
export function instalacionNativaDisponible(): boolean {
  return promptPendiente !== null && !instalada();
}

/** Suscribirse a cambios de disponibilidad (para hooks). Devuelve el unsubscribe. */
export function onInstalacionCambio(cb: () => void): () => void {
  oyentes.add(cb);
  return () => {
    oyentes.delete(cb);
  };
}

/** Debe llamarse desde un toque del usuario. Devuelve "accepted" | "dismissed" | "no-disponible". */
export async function instalarApp(): Promise<"accepted" | "dismissed" | "no-disponible"> {
  const ev = promptPendiente;
  if (!ev) return "no-disponible";
  promptPendiente = null; // el evento solo sirve una vez
  avisar();
  await ev.prompt();
  const { outcome } = await ev.userChoice;
  return outcome;
}
